import React, { ButtonHTMLAttributes, forwardRef } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    loading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            variant = 'primary',
            size = 'md',
            fullWidth = false,
            loading = false,
            leftIcon,
            rightIcon,
            disabled,
            className = '',
            ...props
        },
        ref
    ) => {
        const baseStyles = `
            inline-flex items-center justify-center gap-2
            font-manrope font-semibold
            rounded-lg transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-2
            disabled:cursor-not-allowed
        `;

        const variantStyles = {
            primary: `
                bg-[#015FA6] text-white
                hover:bg-[#014f8c] active:bg-[#013d6d]
                focus:ring-[#015FA6]/50
                disabled:bg-gray-300 disabled:text-gray-500
            `,
            secondary: `
                bg-[#F8F9FB] text-[#015FA6]
                border border-[#CBD5E0]
                hover:bg-[#EDF2F7] active:bg-[#E2E8F0]
                focus:ring-[#015FA6]/30
                disabled:bg-gray-100 disabled:text-gray-400
            `,
            outline: `
                bg-transparent text-[#015FA6]
                border-2 border-[#015FA6]
                hover:bg-[#015FA6]/5 active:bg-[#015FA6]/10
                focus:ring-[#015FA6]/30
                disabled:border-gray-300 disabled:text-gray-400
            `,
            ghost: `
                bg-transparent text-[#015FA6]
                hover:bg-[#015FA6]/10 active:bg-[#015FA6]/20
                focus:ring-[#015FA6]/30
                disabled:text-gray-400
            `
        };

        const sizeStyles = {
            sm: 'h-9 px-3 text-sm',
            md: 'h-[55px] px-6 text-base',
            lg: 'h-14 px-8 text-lg'
        };

        const isDisabled = disabled || loading;

        return (
            <button
                ref={ref}
                disabled={isDisabled}
                className={`
                    ${baseStyles}
                    ${variantStyles[variant]}
                    ${sizeStyles[size]}
                    ${fullWidth ? 'w-full' : ''}
                    ${className}
                `}
                {...props}
            >
                {loading ? (
                    <>
                        <svg
                            className="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                        <span>Loading...</span>
                    </>
                ) : (
                    <>
                        {leftIcon && <span aria-hidden="true">{leftIcon}</span>}
                        {children}
                        {rightIcon && <span aria-hidden="true">{rightIcon}</span>}
                    </>
                )}
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;
